/* Maison REFINE sheet behaviors — price band chips + sheet top offset.
   Works WITH facets.js (FacetFiltersForm): chips write the hidden
   filter.v.price.gte/lte inputs and dispatch a bubbling `input` event so the
   form's debounced AJAX submit runs. Chips are spans (NOT buttons) because
   global.js MenuDrawer binds every button inside the drawer to close it. */
(() => {
  if (!customElements.get('maison-price-bands')) {
    class MaisonPriceBands extends HTMLElement {
      constructor() {
        super();
        this.addEventListener('click', this.onTap.bind(this));
        this.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.onTap(e);
          }
        });
      }
      onTap(e) {
        const chip = e.target.closest('.maison-refine__chip');
        if (!chip) return;
        const min = this.querySelector('input[data-role="min"]');
        const max = this.querySelector('input[data-role="max"]');
        if (!min || !max) return;
        const wasSelected = chip.classList.contains('is-selected');
        this.querySelectorAll('.maison-refine__chip').forEach((c) => c.classList.remove('is-selected'));
        if (wasSelected) {
          min.value = '';
          max.value = '';
        } else {
          chip.classList.add('is-selected');
          min.value = chip.dataset.gte || '';
          max.value = chip.dataset.lte || '';
        }
        min.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }
    customElements.define('maison-price-bands', MaisonPriceBands);
  }

  /* Sheet sits below the sticky header (announcement scrolls away, header is
     sticky "always"): measure the header bottom when the drawer opens and keep
     it fresh on resize/scroll while open. Fallback var default lives in CSS.
     The var is FROZEN while the curtain is dropping/rising (see below) — the
     clip must never relocate mid-motion. */
  let topHeld = false;
  let topHeldAt = 0;
  const setTop = () => {
    /* 2s self-heal: if an end/cancel event was ever missed (node detached by a
       theme-editor re-render mid-animation), don't stay frozen forever */
    if (topHeld && performance.now() - topHeldAt < 2000) return;
    topHeld = false;
    /* curtain drops from beneath the FILTERS & SORT row (design); if the row has
       scrolled under the sticky header, clamp to the header bottom */
    const header = document.getElementById('site-header');
    const row = document.querySelector('.maison-refine-wrapper summary.mobile-facets__open-wrapper');
    let bottom = header ? header.getBoundingClientRect().bottom : 0;
    if (row) bottom = Math.max(bottom, row.getBoundingClientRect().bottom);
    document.documentElement.style.setProperty('--maison-refine-top', Math.max(0, Math.round(bottom)) + 'px');
  };
  /* Resolve the curtain origin BEFORE MenuDrawer opens the <details> (capture
     phase, synchronous). Chrome (iOS) can scroll/focus the summary during the
     open action; when this ran in requestAnimationFrame the sheet started its
     drop from a stale top and the clip relocated one frame later — which reads
     as the curtain coming down twice. Closing taps never re-measure: the rise
     must retract from exactly where the sheet sits. */
  const refineOpen = () => !!document.querySelector('.mobile-facets__disclosure[open]');
  const isMobile = () => window.matchMedia('(max-width: 989px)').matches;
  document.addEventListener('click', (e) => {
    if (!e.target.closest('.mobile-facets__open-wrapper')) return;
    if (!refineOpen()) setTop();
  }, true);
  /* While OPEN the anchor is breakpoint-split:
     — mobile curtain: the page is scroll-locked, so every scroll/resize event
       during the open lifetime is Chrome-iOS browser-chrome noise (URL-bar
       collapse/expand, focus nudges — they change only the viewport HEIGHT).
       The anchor is measured once at the tap and stays frozen until close;
       only a WIDTH change (real rotation/split-view) re-measures. This is what
       kills the residual post-landing slide on Chrome iPhone.
     — desktop band: the page scrolls freely with the band open and the band
       must stay glued under the toolbar row, so scroll keeps updating live. */
  let lastW = window.innerWidth;
  window.addEventListener('resize', () => {
    const w = window.innerWidth;
    const widthChanged = w !== lastW;
    lastW = w;
    if (refineOpen() && isMobile() && !widthChanged) return;
    setTop();
  });
  window.addEventListener('scroll', () => {
    if (!refineOpen() || isMobile()) return;
    setTop();
  }, { passive: true });
  setTop();

  /* Freeze --maison-refine-top while the curtain is in motion (all breakpoints).
     Chrome iOS fires resize/scroll storms from its own browser chrome during
     the .75s drop; each one used to rewrite the var and slide the whole clip
     window mid-animation — the "drops twice" effect. Hold the var from
     animationstart to animationend/cancel of the drop/rise. Desktop re-glues
     once after the drop (the page may really have scrolled mid-animation);
     mobile deliberately does NOT — its anchor stays as measured at the tap. */
  const HOLD_ANIMS = ['maison-refine-drop', 'maison-refine-rise'];
  document.addEventListener('animationstart', (e) => {
    if (HOLD_ANIMS.indexOf(e.animationName) !== -1) {
      topHeld = true;
      topHeldAt = performance.now();
    }
  }, true);
  const releaseTop = (e) => {
    if (HOLD_ANIMS.indexOf(e.animationName) === -1) return;
    topHeld = false;
    if (e.animationName === 'maison-refine-drop' && refineOpen() && !isMobile()) setTop();
  };
  document.addEventListener('animationend', releaseTop, true);
  document.addEventListener('animationcancel', releaseTop, true);
})();

/* Maison: the curtain retract needs .75s but Dawn's MenuDrawer closeAnimation
   removes [open] after ~400ms — extend to 800ms for the refine wrapper only
   (header-drawer has its own shadowing patch). Also sweep the body scroll lock:
   Dawn's closeSubmenu path (X / SHOW buttons) never removes overflow-hidden-mobile. */
window.customElements.whenDefined('menu-drawer').then(() => {
  const proto = window.customElements.get('menu-drawer').prototype;
  if (proto.__maisonRefineClose) return;
  proto.__maisonRefineClose = true;
  /* Open with a scroll-free focus: Dawn's trapFocus focuses the summary, and
     Chrome iOS scrolls it into view — moving the page (and the toolbar row)
     under the sheet during the drop. preventScroll for the refine wrapper only;
     the burger/header drawer keeps stock behavior. */
  const origOpen = proto.openMenuDrawer;
  proto.openMenuDrawer = function (summaryElement) {
    if (
      !this.classList || !this.classList.contains('maison-refine-wrapper') ||
      !summaryElement || typeof summaryElement.focus !== 'function'
    ) {
      return origOpen.call(this, summaryElement);
    }
    const origFocus = summaryElement.focus;
    summaryElement.focus = function (opts) {
      return origFocus.call(this, Object.assign({ preventScroll: true }, opts));
    };
    try {
      return origOpen.call(this, summaryElement);
    } finally {
      summaryElement.focus = origFocus;
    }
  };
  /* Capture the sheet's live transform BEFORE Dawn strips .menu-opening (once that
     class is gone the drop animation stops matching and computed style reads the
     parked value) — the rise keyframe starts from this var, so closing mid-drop
     rises from where the sheet actually is instead of snapping fully open first. */
  const origCloseDrawer = proto.closeMenuDrawer;
  proto.closeMenuDrawer = function (event, elementToFocus) {
    /* Dawn no-ops event-less calls (focusout churn) — don't do capture work for
       a close that won't run; Chrome iOS blurs page focus around taps. */
    if (event === undefined) return origCloseDrawer.call(this, event, elementToFocus);
    if (this.classList && this.classList.contains('maison-refine-wrapper')) {
      const details = this.mainDetailsToggle || this.querySelector('details');
      const inner = this.querySelector('.mobile-facets__inner.maison-refine');
      /* don't recapture while a close is already running — moving the keyframe's
         from-value mid-rise would make the interpolation jump */
      if (details && inner && !details.classList.contains('maison-refine-closing')) {
        const t = getComputedStyle(inner).transform;
        details.style.setProperty('--maison-refine-close-from', t && t !== 'none' ? t : 'translateY(0)');
      }
    }
    return origCloseDrawer.call(this, event, elementToFocus);
  };
  const orig = proto.closeAnimation;
  proto.closeAnimation = function (detailsElement) {
    if (!this.classList || !this.classList.contains('maison-refine-wrapper')) {
      return orig.call(this, detailsElement);
    }
    /* one hold timer at a time — a re-click during the hold routes through
       closeMenuDrawer again ([open] is still set) and must not spawn a second
       timer that could later yank a legitimate reopen shut mid-drop */
    if (detailsElement.__maisonRefineHold) return;
    detailsElement.__maisonRefineHold = true;
    /* closing state drives the reverse curtain CSS; added in the same task as
       Dawn's menu-opening removal, so no un-animated frame can paint between */
    detailsElement.classList.add('maison-refine-closing');
    let start;
    const step = (time) => {
      if (start === undefined) start = time;
      if (time - start < 800) {
        window.requestAnimationFrame(step);
      } else {
        detailsElement.__maisonRefineHold = false;
        if (detailsElement.classList.contains('menu-opening')) {
          /* a legitimate reopen won while we were holding — leave [open] alone */
          detailsElement.classList.remove('maison-refine-closing');
          return;
        }
        detailsElement.removeAttribute('open');
        detailsElement.classList.remove('maison-refine-closing');
        document.body.classList.remove('overflow-hidden-mobile');
      }
    };
    window.requestAnimationFrame(step);
  };
});

/* Colour-name display in the COLOUR header. facets.js swaps only the
   originating group's inner list, never its header, so all interactions use
   delegated listeners and derive the selected fallback from the current DOM.
   Hover and keyboard focus temporarily preview one swatch name; leaving it
   restores the selected names. */
const maisonRefineColourContext = (target) => {
  const swatch = target && target.closest ? target.closest('.maison-refine__swatch') : null;
  const wrap = swatch && swatch.closest('.maison-refine-wrapper');
  const nameEl = wrap && wrap.querySelector('.maison-refine__colour-name');
  return { swatch, wrap, nameEl };
};
const maisonRefineColourListContext = (target) => {
  const list = target && target.closest ? target.closest('.maison-refine__swatches') : null;
  const wrap = list && list.closest('.maison-refine-wrapper');
  const nameEl = wrap && wrap.querySelector('.maison-refine__colour-name');
  return { list, wrap, nameEl };
};
const maisonRefineSelectedColourNames = (wrap) => Array.from(
  wrap ? wrap.querySelectorAll('.maison-refine__swatch input:checked') : []
)
  .map((input) => {
    const label = input.closest('.maison-refine__swatch');
    return label ? label.getAttribute('title') : '';
  })
  .filter(Boolean);
const maisonRefineRestoreColourName = (wrap, nameEl) => {
  if (!nameEl) return;
  nameEl.textContent = maisonRefineSelectedColourNames(wrap).join(' · ');
};
const maisonRefinePreviewColourName = (target) => {
  const { swatch, nameEl } = maisonRefineColourContext(target);
  if (!swatch || !nameEl) return;
  nameEl.textContent = swatch.getAttribute('title') || '';
};

document.addEventListener('change', (e) => {
  if (!e.target || !e.target.matches || !e.target.matches('.maison-refine__swatch input')) return;
  const { wrap, nameEl } = maisonRefineColourContext(e.target);
  maisonRefineRestoreColourName(wrap, nameEl);
});
document.addEventListener('pointerover', (e) => {
  maisonRefinePreviewColourName(e.target);
});
document.addEventListener('pointerout', (e) => {
  const { list, wrap, nameEl } = maisonRefineColourListContext(e.target);
  /* Keep the current preview while the pointer crosses the 12px gaps or enters
     another swatch. Restore the selected names only after leaving the whole row. */
  if (!list || list.contains(e.relatedTarget)) return;
  maisonRefineRestoreColourName(wrap, nameEl);
});
document.addEventListener('focusin', (e) => {
  maisonRefinePreviewColourName(e.target);
});
document.addEventListener('focusout', (e) => {
  const { list, wrap, nameEl } = maisonRefineColourListContext(e.target);
  if (!list || list.contains(e.relatedTarget)) return;
  maisonRefineRestoreColourName(wrap, nameEl);
});
